import { ActorRefFrom, assign, createMachine, sendParent, spawn } from 'xstate';

import {
  aclOptions,
  EXPIRED_TOKEN,
  fileAcceptTypes,
  getAPIErrorMessage,
  getS3UploadedFileName,
  OVER_RIDE_BUCKET_PATH
} from '@nw/shared/utils';

import { failureToastView } from '../../../../ui/src';

import FileUploadConfiguration from '../../stores/models/FileUploadConfiguration';
import S3FileUploader from '../../stores/S3FileUploader';

import { fileUploaderMachine } from '../FileUploaderMachine/FileUploaderMachine';

import {
  events,
  FILE_UPLOADERS_MACHINE_ID,
  events as fileUploaderMachineEvents,
  states as machineStates
} from './constants';

export interface FileUplodersMachineContext {
  configuredAWSS3Client: any;
  fileUploaders: Array<any>;
  configDetails;
  uuid: string;
  overrideBucketPath?: string;
}

export type FileUploadersMachineEvent =
  | { type: 'GET_AWS_CREDENTIALS' }
  | {
      type: 'SPAWN_NEW_FILE_UPLOADER';
      id: string;
      assignActor: (actor) => void;
      aclType: string;
      getS3FileUploaderUrl: (url: string) => string;
    }
  | { type: 'ON_SUCCESS_GETTING_FILE_UPLOADER_CONFIG' }
  | { type: 'ON_FAILURE_GETTING_FILE_UPLOADER_CONFIG' };

export const fileUploadersMachine = createMachine<
  FileUplodersMachineContext,
  FileUploadersMachineEvent
>(
  {
    id: FILE_UPLOADERS_MACHINE_ID,
    initial: machineStates.initial,
    context: {
      configuredAWSS3Client: null,
      fileUploaders: [],
      uuid: '',
      configDetails: {},
      overrideBucketPath: ''
    },
    on: { SPAWN_NEW_FILE_UPLOADER: { actions: 'spawnNewFileUploader' } },
    states: {
      [machineStates.initial]: {
        on: {
          [events.getAWSCredentials]: [machineStates.getAWSCredentials]
        }
      },
      [machineStates.getAWSCredentials]: {
        invoke: {
          src: 'getAWSCredentials',
          onDone: {
            actions: [
              'updateConfigDetails'
              //   sendParent({ type: events.onSuccessGettingFileUploaderConfig })
            ],
            target: machineStates.configureAWS
          },
          onError: {
            actions: [sendParent(events.onFailureGettingFileUploaderConfig)],
            target: machineStates.getAWSCredentialsFailure
          }
        }
      },
      [machineStates.configureAWS]: {
        entry: 'updateS3Client',
        on: {
          '': [
            {
              cond: 'isChildrenAlreadySpawned',
              target: machineStates.sendUpdatedConfigDetailsToChildren
            }
          ]
        }
      },
      [machineStates.sendUpdatedConfigDetailsToChildren]: {
        entry: (context) =>
          context.fileUploaders.forEach((eachUploader) => {
            eachUploader.send({
              type: fileUploaderMachineEvents.updateChildrenS3ClientDetails,
              data: {
                configuredAWSS3Client: context.configuredAWSS3Client,
                configDetails: context.configDetails
              }
            });
          }),
        on: {
          [events.getAWSCredentials]: machineStates.getAWSCredentials
        }
      },
      [machineStates.getAWSCredentialsFailure]: {
        on: {
          [events.getAWSCredentials]: [machineStates.getAWSCredentials]
        }
      }
    }
  },
  {
    actions: {
      updateConfigDetails: assign({
        configDetails: (context, event: any) =>
          new FileUploadConfiguration(event.data)
      }),
      updateS3Client: assign({
        configuredAWSS3Client: (context) => {
          const s3FileUpload = new S3FileUploader();
          s3FileUpload.configureAWS(context.configDetails);
          return s3FileUpload;
        }
      }),
      spawnNewFileUploader: assign({
        fileUploaders: (context, event: any) => {
          const {
            id,
            assignActor,
            fileAcceptTypes: acceptTypes,
            aclType,
            getS3FileUploaderUrl
          } = event;

          const fileUploaderActions = event.fileUploaderActions ?? {};
          const fileUploaderActor = spawn(
            fileUploaderMachine(id)
              .withConfig({
                guards: {
                  isExpiredToken: (fileUploaderContext, event: any) =>
                    event.data.Code === EXPIRED_TOKEN,
                  isFailedToUpload: (fileUploaderContext) =>
                    fileUploaderContext.isFailedToUpload,
                  shouldDeleteFromS3Bucket: (fileUploaderContext) =>
                    fileUploaderContext.deleteFromS3,
                  isValidFileExtension: (fileUploaderContext, event: any) => {
                    const {
                      fileUploadDetails: { fileAcceptTypes }
                    } = fileUploaderContext;
                    const fileAcceptTypesArray = fileAcceptTypes.split(',');
                    const regex = new RegExp('[^.]+$');
                    const extension = event.imgDetails.name.match(regex);
                    return fileAcceptTypesArray.includes(
                      `.${extension}`.toLowerCase()
                    );
                  },
                  isNotValidFileExtension: (
                    fileUploaderContext,
                    event: any
                  ) => {
                    const {
                      fileUploadDetails: { fileAcceptTypes }
                    } = fileUploaderContext;
                    const fileAcceptTypesArray = fileAcceptTypes.split(',');
                    const regex = new RegExp('[^.]+$');
                    const extension = event.imgDetails.name.match(regex);
                    return !fileAcceptTypesArray.includes(
                      `.${extension}`.toLowerCase()
                    );
                  }
                },
                services: {
                  uploadToAWS: (fileUploaderContext) =>
                    fileUploaderContext.configuredAWSS3Client.uploadToAWS(
                      {
                        extraBucketPath: '',
                        overrideBucketPath: OVER_RIDE_BUCKET_PATH,
                        file: fileUploaderContext.selectedFileDetails,
                        uuid: context.uuid,
                        getCustomFileName:
                          fileUploaderContext.getCustomFileName(
                            fileUploaderContext.extraBucketPath,
                            '',
                            fileUploaderContext.selectedFileDetails,
                            fileUploaderContext.configDetails,
                            context.uuid
                          ),
                        acl: aclType ? aclType : aclOptions.publicRead,
                        getS3FileUploaderUrl: getS3FileUploaderUrl
                      },
                      fileUploaderContext.configDetails
                    ),
                  uploadToBackend: (fileUploaderContext) => Promise.resolve(),
                  removeFile: () => Promise.resolve(),
                  deleteFromS3Bucket: (fileUploaderContext) =>
                    fileUploaderContext.configuredAWSS3Client.deleteFromS3Bucket(
                      fileUploaderContext.s3UploadedFileInfo,
                      fileUploaderContext.configDetails
                    ),
                  abortUploadingToAWS: (fileUploaderContext) =>
                    fileUploaderContext.configuredAWSS3Client.abortUploading()
                },
                actions: {
                  clearSelectedFileDetails: assign((context) => {
                    context.selectedFileDetails = () => ({});
                    context.s3UploadedFileInfo = () => ({});
                    return context;
                  }),
                  assignSelectedFileDetails: assign((context, event: any) => ({
                    selectedFileDetails: event.imgDetails,
                    extraBucketPath: event.extraBucketPath,
                    getCustomFileName: event.getCustomFileName
                      ? event.getCustomFileName
                      : context.getCustomFileName
                  })),
                  updateUploadedFileInfo: assign({
                    s3UploadedFileInfo: (context, event: any) => event.data
                  }),
                  errorInfo: assign((context) => ({
                    ...context,
                    isFailedToUpload: Boolean(true)
                  })),
                  removeFailedToUploadState: assign((context) => ({
                    ...context,
                    isFailedToUpload: Boolean(false)
                  })),
                  updateS3ClientAndConfigDetails: assign({
                    configDetails: (context, event: any) =>
                      event.data.configDetails,
                    configuredAWSS3Client: (context, event: any) =>
                      event.data.configuredAWSS3Client
                  }),
                  showErrorToast: (_context, event: any) => {
                    failureToastView(getAPIErrorMessage(event.data));
                  },
                  showInvalidFileExtensionToast: (_context, event: any) => {
                    failureToastView(
                      'Please provide the file with the valid extension'
                    );
                  },
                  ...fileUploaderActions
                }
              })
              .withContext({
                configuredAWSS3Client: context.configuredAWSS3Client,
                selectedFileDetails: {},
                configDetails: context.configDetails,
                isFailedToUpload: false,
                s3UploadedFileInfo: {},
                deleteFromS3: false,
                extraBucketPath: event.extraBucketPath,
                getCustomFileName: event.getCustomFileName
                  ? event.getCustomFileName
                  : getS3UploadedFileName,
                fileUploadDetails: {
                  fileAcceptTypes: acceptTypes ? acceptTypes : fileAcceptTypes
                }
              })
          );
          assignActor(fileUploaderActor);
          return [...context.fileUploaders, fileUploaderActor];
        }
      })
    },
    guards: {
      isChildrenAlreadySpawned: (context) =>
        Boolean(context.fileUploaders.length > 0)
    }
  }
);

export type AdAssetsActor = ActorRefFrom<typeof fileUploadersMachine>;
